/**
 * Helper functions for ticket validation and pricing
 */

/**
 * Check if the selected seat is compatible with the ticket type
 * @param {string|null} ticketSeatId - The selected seat ID
 * @param {Object|null} ticketType - The ticket type object with seats array
 * @returns {boolean} - True if seat is compatible or no validation needed
 */
export function isSeatCompatibleWithTicketType(ticketSeatId, ticketType) {
    // If no seat selected or no ticket type, no validation needed
    if (!ticketSeatId || !ticketType) {
        return true;
    }

    // If ticket type has no seats array or it's empty, all seats are allowed
    if (!ticketType.seats || ticketType.seats.length === 0) {
        return true;
    }

    // Check if the seat is in the allowed seats list
    return ticketType.seats.some((seat) => seat.id === ticketSeatId);
}

/**
 * Check if a ticket option can be sold with the ticket type
 * @param {string|null} ticketOptionName - The ticket option name
 * @param {Object|null} priceMatrix - The price matrix from ticket type
 * @returns {boolean} - True if combination can be sold
 */
export function isTicketCombinationValid(ticketOptionName, priceMatrix) {
    // If no option selected or no price matrix, assume valid
    if (!ticketOptionName || !priceMatrix) {
        return true;
    }

    // Check if the option exists in the price matrix (value can be 0, but key must exist)
    return Object.prototype.hasOwnProperty.call(priceMatrix, ticketOptionName);
}

/**
 * Get the price for a ticket option from the price matrix
 * @param {string|null} ticketOptionName - The ticket option name
 * @param {Object|null} priceMatrix - The price matrix from ticket type
 * @returns {number|null} - The price in cents, or null if not found
 */
export function getPriceFromMatrix(ticketOptionName, priceMatrix) {
    if (!ticketOptionName || !priceMatrix) {
        return null;
    }

    if (Object.prototype.hasOwnProperty.call(priceMatrix, ticketOptionName)) {
        return priceMatrix[ticketOptionName];
    }

    return null;
}

/**
 * Build a per-timeslot price-override lookup map from a getTimeslotPrices
 * response. Keyed by `${ticketTypeId}:${ticketOptionId}` -> price (cents).
 * @param {{prices?: Array<{ticketTypeId:number, ticketOptionId:number, price:number}>}|null} timeslotPricesData
 * @returns {Map<string, number>}
 */
export function buildPriceOverrideMap(timeslotPricesData) {
    const map = new Map();
    (timeslotPricesData?.prices || []).forEach((p) => {
        map.set(`${p.ticketTypeId}:${p.ticketOptionId}`, p.price);
    });
    return map;
}

/**
 * Resolve the effective price (cents) for a ticket type/option, preferring the
 * per-timeslot override and falling back to the ticket type's default matrix.
 * @param {number} ticketTypeId
 * @param {number} ticketOptionId
 * @param {string|null} ticketOptionName
 * @param {Object|null} priceMatrix - The ticket type's price matrix
 * @param {Map<string, number>|undefined} priceOverrides
 * @returns {number|null} - price in cents, or null if unresolved
 */
export function resolvePriceCents(
    ticketTypeId,
    ticketOptionId,
    ticketOptionName,
    priceMatrix,
    priceOverrides,
) {
    const override = priceOverrides?.get(`${ticketTypeId}:${ticketOptionId}`);
    if (override !== undefined) {
        return override;
    }
    return getPriceFromMatrix(ticketOptionName, priceMatrix);
}

/**
 * Get validation warnings for current ticket configuration
 * @param {Object} values - Current ticket values
 * @param {Object} ticketType - Selected ticket type object
 * @param {string} ticketOptionName - Selected ticket option name
 * @param {Function} t - i18n translation function
 * @returns {Object} - Object with warning flags and messages
 */
export function getTicketValidationWarnings(values, ticketType, ticketOptionName, t) {
    const warnings = {
        seatMismatch: false,
        invalidCombination: false,
        priceDifference: false,
        messages: [],
    };

    // Check seat compatibility
    if (
        values.ticketSeatId &&
        ticketType &&
        !isSeatCompatibleWithTicketType(values.ticketSeatId, ticketType)
    ) {
        warnings.seatMismatch = true;
    }

    // Check if combination can be sold
    if (
        ticketOptionName &&
        ticketType?.priceMatrix &&
        !isTicketCombinationValid(ticketOptionName, ticketType.priceMatrix)
    ) {
        warnings.invalidCombination = true;
        warnings.messages.push(t('bookingitems.validation.invalidCombination'));
    }

    // Check if custom price differs from suggested price
    if (
        ticketOptionName &&
        ticketType?.priceMatrix &&
        isTicketCombinationValid(ticketOptionName, ticketType.priceMatrix) &&
        values.customPriceCents !== undefined &&
        values.customPriceCents !== null &&
        values.customPriceCents !== ''
    ) {
        const suggestedPrice = getPriceFromMatrix(ticketOptionName, ticketType.priceMatrix);
        if (suggestedPrice !== null && suggestedPrice !== values.customPriceCents) {
            warnings.priceDifference = true;
            warnings.messages.push(
                t('bookingitems.validation.priceDifference', {
                    price: `$${(suggestedPrice / 100).toFixed(2)}`,
                }),
            );
        }
    }

    return warnings;
}

/**
 * Update price field based on ticket type and option combination
 * @param {number|string} ticketTypeId - The ticket type ID
 * @param {string} ticketOptionName - The ticket option name
 * @param {Array} allTicketTypes - Array of all ticket types
 * @param {Function} setFieldValue - Function to update the price field
 * @returns {void}
 */
export function updatePriceFromMatrix(
    ticketTypeId,
    ticketOptionName,
    allTicketTypes,
    setFieldValue,
    ticketOptionId,
    priceOverrides,
) {
    const ticketType = allTicketTypes?.find((type) => type.id === ticketTypeId);
    if (ticketType?.priceMatrix && ticketOptionName) {
        // Prefer the per-timeslot override; fall back to the ticket type default.
        const override = priceOverrides?.get(`${ticketTypeId}:${ticketOptionId}`);
        const price =
            override !== undefined
                ? override
                : getPriceFromMatrix(ticketOptionName, ticketType.priceMatrix);
        const newPrice = price !== null && price !== undefined ? price : '';
        setFieldValue('customPriceCents', newPrice);
    }
}
